/**
 * ArtsAwardsList — /arts/awards
 * Directory of major awards (Oscars, Grammys, Palme d'Or, Bharat
 * Ratna …). Click into any one to see editions across the years.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Award, Search, PenSquare } from "lucide-react";
import { http } from "@/lib/api";

export default function ArtsAwardsList() {
  const [data, setData] = useState({ total: 0, items: [] });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const url = q ? `/arts/awards?q=${encodeURIComponent(q)}&limit=48` : "/arts/awards?limit=48";
        const r = await http.get(url).then(r => r.data);
        if (alive) setData(r);
      } catch {
        if (alive) setData({ total: 0, items: [] });
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [q]);

  return (
    <div className="max-w-5xl mx-auto px-4 py-8" data-testid="arts-awards-list">
      <Link to="/arts" className="text-xs opacity-70 hover:opacity-100 inline-flex items-center gap-1 mb-3">
        <ArrowLeft className="w-3 h-3" /> Arts hub
      </Link>
      <div className="flex items-end justify-between gap-4 flex-wrap mb-6">
        <div>
          <div className="overline mb-1" style={{ color: "var(--wofy-primary)" }}>DIRECTORY</div>
          <h1 className="font-display text-3xl md:text-4xl inline-flex items-center gap-2">
            <Award className="w-7 h-7" /> Awards
          </h1>
          <p className="opacity-80 mt-1">
            The world's most prestigious cultural and creative awards — and everyone who has ever won.
          </p>
        </div>
        <Link to="/arts/awards/submit" className="wofy-btn-primary" data-testid="arts-award-add">
          <PenSquare className="w-4 h-4" /> Add an award
        </Link>
      </div>

      <div className="relative mb-6 max-w-md">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 opacity-60" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search awards…"
          className="w-full pl-9 pr-3 py-2 rounded-lg border bg-transparent text-sm"
          style={{ borderColor: "var(--wofy-border)" }}
          data-testid="arts-awards-search"
        />
      </div>

      {loading && <div className="text-sm opacity-70">Loading…</div>}
      {!loading && data.items.length === 0 && (
        <div className="wofy-card !p-6 opacity-80 text-center">No awards yet.</div>
      )}
      {!loading && data.items.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="arts-awards-grid">
          {data.items.map((a) => (
            <Link
              key={a.slug}
              to={`/arts/awards/${a.slug}`}
              data-testid={`arts-award-${a.slug}`}
              className="group rounded-xl border p-4 transition hover:bg-black/5"
              style={{ borderColor: "var(--wofy-border)" }}
            >
              <div className="flex items-start justify-between mb-1">
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-wider opacity-60">
                    {a.kind}{a.country ? ` · ${a.country}` : ""}
                  </div>
                  <div className="font-medium text-lg leading-tight group-hover:underline">{a.name}</div>
                </div>
                <div className="text-[10px] font-mono opacity-70">
                  {a.edition_count}× · latest {a.latest_year || "—"}
                </div>
              </div>
              {a.description && (
                <p className="text-xs opacity-75 line-clamp-2 mt-1">{a.description}</p>
              )}
              {a.presenting_body && (
                <div className="text-[11px] opacity-60 mt-2">Presented by {a.presenting_body}</div>
              )}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
